<script>
  // ── Das Zutat-Detail (A7/L7) ──────────────────────────────────────────────
  //
  // Es beantwortet zwei Fragen, die die Liste offenlässt: Woher kommt diese
  // Zahl? Und: Wie werde ich eine falsche Buchung wieder los?
  //
  // Die erste beantwortet das Journal — jede Zeile sagt, WOHER sie kommt, und
  // daraus entsteht das Vertrauen in den Bestand oben. Die zweite beantwortet
  // das Sheet (17.7), das der Entwurf nicht zeichnet.
  //
  // EIN MARKUP, ZWEI BREITEN — wie überall im Modul (1.5, Vorschlag 2). Der
  // Bruch liegt auf dem CONTAINER, nicht auf dem Viewport: Diese Ansicht setzt
  // ihn selbst, weil sie die Bestandsansicht ERSETZT und deren Kasten damit
  // nicht mehr da ist.
  //
  // WAS SIE NICHT TUT (Plan 17.9)
  // Keine Meldeschwelle (US-5, zweiter Teil), kein Bearbeiten von Buchungen —
  // append-only bleibt append-only.
  //
  // „Bestand richtigstellen" (US-4) steht seit L8 hier: als leiser Knopf unter
  // der großen Zahl, die er in Frage stellt. Der Entwurf setzt ihn „unter den
  // CTA" — den gibt es hier nicht (s. unten), der Platz war also frei, aber
  // nicht bestimmt.
  //
  // „Eingang buchen" steht seit Plan 18.6 daneben — so, wie beide Entwürfe es
  // zeichnen. Der Vorbehalt von L8 ist damit eingelöst: Die Maske kennt jetzt
  // eine Vorbelegung (`vorwahl`), der Knopf führt also nicht mehr in dieselbe
  // LEERE Maske wie der CTA der Liste, sondern auf die Zutat, die hier offen
  // ist — mit dem Cursor in der Menge.
  import { onMount } from 'svelte';
  import Reichweite from '../teile/Reichweite.svelte';
  import BuchungSheet from '../teile/BuchungSheet.svelte';
  import RichtigstellenSheet from '../teile/RichtigstellenSheet.svelte';
  import ZutatSheet from '../teile/ZutatSheet.svelte';

  /** @type {{ zutatId: string, onzurueck: () => void, oneingang: (id: string) => void }} */
  let { zutatId, onzurueck, oneingang } = $props();

  const t  = (k, p) => window.t(k, p);
  const tn = (k, n, p) => window.tn(k, n, p);

  /** Ohne Kappung wäre das Journal einer alten Zutat eine Wand. Anders als die
   *  Kappung der LISTE (17.1, vertagt) wächst es unbegrenzt: Ein Bestand hat
   *  ein bis drei Dutzend Zutaten, eine Zutat aber beliebig viele Buchungen. */
  const ERST = 8;

  let zeile   = $state(/** @type {any} */ (null));
  let zutat   = $state(/** @type {any} */ (null));
  let verlauf = $state(/** @type {any[]} */ ([]));
  let alle    = $state(false);
  let offen   = $state(/** @type {any} */ (null));
  let korrOffen = $state(false);
  let zutatOffen = $state(false);

  function neu() {
    // Dieselbe Ableitung wie die Liste — die beiden können deshalb gar nicht
    // verschiedene Zahlen zeigen.
    zeile   = window.lagerBestandsliste().find((z) => z.zutatId === zutatId) || null;
    // Als `$derived` über `window.zutatById()` rechnete das nie neu: Svelte
    // kann einen Global nicht beobachten, und `zutatId` ändert sich hier nie.
    // Nach einem Umbenennen (E-11) stand deshalb weiter der alte Name im Kopf.
    // Gehört damit hierher, zu allem anderen, was `zutat-stamm:changed` hört.
    zutat   = window.zutatById(zutatId);
    verlauf = window.lagerVerlauf(zutatId);
    // Eine zurückgenommene Buchung verschwindet aus dem Sheet nicht, sie
    // ändert ihren Zustand — also die frische Fassung nachziehen.
    if (offen) offen = verlauf.find((b) => b.id === offen.id) || null;
  }

  onMount(() => {
    neu();
    document.addEventListener('lager:journal-geaendert', neu);
    document.addEventListener('zutat-stamm:changed', neu);
    // Ein geplanter Backtag ist Bedarf, auch ohne Buchung (A7/L5).
    document.addEventListener('backtage:geaendert', neu);
    return () => {
      document.removeEventListener('lager:journal-geaendert', neu);
      document.removeEventListener('zutat-stamm:changed', neu);
      document.removeEventListener('backtage:geaendert', neu);
    };
  });

  let menge   = $derived(window.lagerMenge(zeile?.bestand ?? 0));
  // Mit der Zutat, nicht ohne: Erst damit kann die Anzeige die Schwund-Zeile
  // stellen (Plan 18). Die LISTE fragt bewusst ohne — dort stünde derselbe
  // Nebensatz 27-mal.
  let anzeige = $derived(window.lagerReichweiteAnzeige(zeile?.prognose, zutatId));
  let sichtbar = $derived(alle ? verlauf : verlauf.slice(0, ERST));

  function stand(gramm) {
    const m = window.lagerMenge(gramm);
    return t('lager_detail_stand', { menge: `${m.wert} ${m.einheit}` });
  }

  function delta(gramm) {
    const m = window.lagerMenge(Math.abs(gramm));
    return `${gramm < 0 ? '−' : '+'}${m.wert} ${m.einheit}`;
  }
</script>

<div class="lg-detail">
  <!-- Der Zurück-Weg steht auf beiden Breiten an derselben Stelle. Der Entwurf
       hat mobil einen Pfeil in einer eigenen Kopfzeile — die ist Mockup-Gerüst
       (1.5), der Knopf ist es nicht (Plan 17.6). -->
  <button type="button" class="lg-zurueck" onclick={onzurueck}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
         stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
    {t('lager_detail_zurueck')}
  </button>

  <header class="lg-dkopf">
    <div class="lg-dkopf__text">
      <div class="lg-dnamezeile">
        <h1 class="lg-dname">{zutat?.name || zeile?.name || t('lager_detail_unbekannt')}</h1>
        <!-- Berichtigen sitzt AM NAMEN, nicht bei den Handlungen unter der
             Zahl: Umbenennen und Archivieren betreffen die Zutat, nicht ihren
             Bestand. Nur wo es eine Stammzeile gibt — eine Zutat, die nur im
             Rezept steht, hat nichts zu berichtigen (Review 5, E-11). -->
        {#if zutat}
          <button type="button" class="lg-dstift" onclick={() => (zutatOffen = true)}
                  aria-label={t('lager_zutat_bearbeiten')} title={t('lager_zutat_bearbeiten')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
                 stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true">
              <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
            </svg>
          </button>
        {/if}
      </div>
      <!-- Nur die Warengruppe. Der Chip „rechnet als Mehl" aus dem Entwurf ist
           hier weg (Plan 17.16): Die Rechenrolle gehoert in die REZEPTRECHNUNG,
           wo sie etwas entscheidet. Im Lager entscheidet sie nichts — der
           Bestand ist Gramm, und die Reichweite rechnet aus Buchungen. -->
      <div class="lg-dmarken">
        <span class="lg-marke">{zeile?.warengruppe || zutat?.warengruppe || '—'}</span>
      </div>
    </div>

    <div class="lg-dwert">
      <span class="lg-dwert__label">{t('lager_detail_bestand')}</span>
      <span class="lg-dwert__zahl">
        <b>{menge.wert}</b><span class="lg-dwert__einheit">{menge.einheit}</span>
      </span>
      <span class="lg-dwert__basis">{tn('lager_detail_basis', zeile?.buchungen ?? 0)}</span>

      <!-- Zwei Handlungen, eine Zeile, klare Rangfolge. „Eingang buchen" ist
           das, was am häufigsten passiert, und trägt deshalb die Akzentfarbe —
           dieselbe wie der CTA der Liste, nur kleiner: Das Detail ist keine
           Erfassungsmaske, es ist eine Auskunft mit zwei Ausgängen.
           „Bestand korrigieren" bleibt die Stufe darunter; richtiggestellt
           wird seltener als gebucht. Beide stehen direkt unter der Zahl —
           dort entsteht sowohl der Zweifel als auch der Nachschub.
           Als unterstrichener Text war die Korrektur zu leise: Im Prüfblatt
           (2026-09-10) war sie zwar zu finden, aber nicht als Bedienelement zu
           erkennen. Seitdem trägt sie Rahmen und Fläche. -->
      <div class="lg-dtaten">
        <button type="button" class="lg-dein" onclick={() => oneingang(zutatId)}>
          {t('lager_eingang_oeffnen')}
        </button>
        <button type="button" class="lg-dkorr" onclick={() => (korrOffen = true)}>
          {t('lager_detail_richtigstellen')}
        </button>
      </div>
    </div>
  </header>

  <!-- Steht in JEDEM Zustand da, auch wenn der Bestand reicht: Eine Prognose,
       die nur bei Fehlmengen erscheint, liest sich als Alarm (Kit-Kommentar zu
       `.md-reich--desk`). -->
  <Reichweite {anzeige} dichte="tafel" />

  <h2 class="lg-dabschnitt">{t('lager_detail_verlauf')}</h2>

  {#if verlauf.length === 0}
    <p class="lg-dleer">{t('lager_detail_leer')}</p>
  {:else}
    <div class="lg-dkarte">
      {#each sichtbar as b (b.id)}
        <!-- Die ganze Zeile ist die Trefferfläche (Fitts) — dasselbe Prinzip
             wie `.a-row__hit` in der Liste, nur hier als Knopf statt als
             Overlay, weil es kein Ziel gibt, das ein Link wäre. -->
        <button type="button" class="lg-buch" class:is-storno={b.storniert}
                onclick={() => (offen = b)}>
          <!-- Grün heißt „ist hereingekommen". Eine Zählung ist das nicht —
               sie bleibt neutral, sonst läse sich eine positive Differenz als
               Wareneingang und eine negative als verbacken (US-4). -->
          <span class="lg-buch__icon"
                class:is-ein={(b.menge ?? 0) >= 0 && b.art !== 'inventur'} aria-hidden="true">
            {#if b.storniert}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round" width="14" height="14">
                <path d="M3 2v6h6"/><path d="M3 13a9 9 0 1 0 3-7.7L3 8"/>
              </svg>
            {:else if b.art === 'inventur'}
              <!-- Waage: gewogen und für zu leicht befunden — oder für zu
                   schwer. Das Zeichen sagt „hier wurde gemessen", nicht
                   „hier ist etwas geflossen". -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
                   stroke-linecap="round" stroke-linejoin="round" width="14" height="14">
                <path d="M12 3v18"/><path d="M5 7h14"/>
                <path d="M5 7 2 13a3 3 0 0 0 6 0Z"/><path d="M19 7l-3 6a3 3 0 0 0 6 0Z"/>
              </svg>
            {:else if (b.menge ?? 0) >= 0}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" width="14" height="14">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            {:else}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" width="14" height="14">
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            {/if}
          </span>

          <span class="lg-buch__text">
            <span class="lg-buch__art">{b.titel}</span>
            <span class="lg-buch__meta">{b.meta}</span>
          </span>

          <span class="lg-buch__zahlen">
            <span class="lg-buch__delta"
                  class:is-ein={(b.menge ?? 0) >= 0 && b.art !== 'inventur'}>{delta(b.menge ?? 0)}</span>
            <!-- Der laufende Saldo ist eine MOBIL-Entscheidung (Plan 17.5):
                 Auf 390 px liest man eine Zeile nach der anderen und braucht
                 den Anker; breit steht der Bestand groß im Kopf und die Spalte
                 wäre Wiederholung. Eine stornierte Zeile hat gar keinen. -->
            {#if b.standDanach != null}
              <span class="lg-buch__stand">{stand(b.standDanach)}</span>
            {/if}
          </span>
        </button>
      {/each}

      {#if !alle && verlauf.length > ERST}
        <button type="button" class="lg-dalle" onclick={() => (alle = true)}>
          {tn('lager_detail_alle_buchungen', verlauf.length)}
        </button>
      {/if}
    </div>
  {/if}

  <p class="lg-dnote">{t('lager_eingang_journal_note')}</p>
</div>

{#if korrOffen}
  <RichtigstellenSheet {zutatId} onschliessen={() => (korrOffen = false)} />
{/if}

{#if zutatOffen && zutat}
  <ZutatSheet
    {zutatId}
    bestand={zeile?.bestand ?? 0}
    onschliessen={() => (zutatOffen = false)}
    onarchiviert={() => { zutatOffen = false; onzurueck(); }}
  />
{/if}

{#if offen}
  <BuchungSheet
    buchung={offen}
    onschliessen={() => (offen = null)}
    onzurueckgenommen={() => (offen = null)}
  />
{/if}

<style>
  /* Diese Ansicht ERSETZT die Bestandsansicht und muss den Container deshalb
     selbst stellen — Reichweite.svelte fragt ihn per @container (12.1). */
  .lg-detail {
    container-type: inline-size;
    max-width: 1000px;
    margin: 0 auto;
    padding: 12px 0 24px;
  }

  .lg-zurueck {
    display: inline-flex; align-items: center; gap: 6px;
    min-height: 36px; margin: 0 16px 4px; padding: 0 10px 0 6px;
    border: 0; border-radius: 8px; background: none;
    font: inherit; font-size: 13px; font-weight: 500; color: var(--lg-ink-mittel);
    cursor: pointer;
  }
  .lg-zurueck:hover { background: rgba(41, 37, 36, 0.05); color: var(--lg-ink); }
  .lg-zurueck:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }

  /* ── Kopf ─────────────────────────────────────────────────────────────── */
  .lg-dkopf { padding: 4px 16px 16px; }
  .lg-dkopf__text { min-width: 0; }
  /* Der Stift steht NEBEN dem Namen, nicht darunter: Er gehoert zu ihm, und
     eine eigene Zeile haette auf 390 px Platz gekostet, den der Name braucht.
     `align-items: start`, damit er bei einem zweizeiligen Namen oben bleibt. */
  .lg-dnamezeile { display: flex; align-items: start; gap: 6px; }
  .lg-dname {
    margin: 0 0 6px;
    font-size: 22px; font-weight: 700; letter-spacing: -0.02em; color: var(--lg-ink);
    overflow-wrap: anywhere;
    min-width: 0;
  }
  .lg-dstift {
    flex: none; width: 30px; height: 30px; margin-top: -2px;
    display: grid; place-items: center;
    border: 0; background: none; border-radius: 8px;
    color: var(--lg-ink-mute); cursor: pointer;
  }
  .lg-dstift:hover { background: var(--lg-fill); color: var(--lg-ink); }
  .lg-dmarken { display: flex; flex-wrap: wrap; gap: 6px; }
  .lg-marke {
    display: inline-flex; align-items: center;
    padding: 2px 10px; border-radius: 999px;
    border: 1px solid var(--lg-line); background: var(--lg-card);
    font-size: 11px; font-weight: 600; color: var(--lg-ink-mittel);
  }

  /* Schmal steht der Bestand UNTER dem Namen und ist die größte Zahl der
     Seite (38 px, `.md-hero__num`) — so, wie die Zutat im Regal steht. */
  .lg-dwert { display: block; margin-top: 20px; }
  .lg-dwert__label {
    display: block;
    font-size: 11px; font-weight: 700; letter-spacing: 0.09em;
    text-transform: uppercase; color: var(--lg-ink-mute);
  }
  .lg-dwert__zahl { display: flex; align-items: baseline; gap: 6px; margin-top: 4px; }
  .lg-dwert__zahl b {
    font-size: 38px; font-weight: 700; line-height: 1; letter-spacing: -0.03em;
    color: var(--lg-ink); font-variant-numeric: tabular-nums;
  }
  .lg-dwert__einheit { font-size: 15px; font-weight: 600; color: var(--lg-ink-mittel); }
  .lg-dwert__basis {
    display: block; margin-top: 8px;
    font-size: 11.5px; color: var(--lg-ink-mute);
  }
  /* Umbrechend, nicht schrumpfend: Auf 390 px stehen „Eingang buchen" und
     „Bestand korrigieren" zusammen an der Grenze der Zeilenbreite, und ein
     gequetschter Knopf ist schlechter als zwei untereinander. */
  .lg-dtaten { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }

  .lg-dein, .lg-dkorr {
    display: inline-flex; align-items: center; gap: 6px;
    min-height: 40px; padding: 0 14px;
    border-radius: 10px;
    font: inherit; font-size: 13.5px; font-weight: 600;
    cursor: pointer;
  }
  .lg-dein {
    border: 1px solid transparent;
    background: var(--lg-accent); color: #fff; font-weight: 700;
    box-shadow: 0 6px 16px -10px rgba(192, 90, 62, 0.6);
  }
  .lg-dein:hover { background: var(--lg-accent-hover); }
  .lg-dein:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }
  .lg-dkorr {
    border: 1px solid #d6d3d1; background: var(--lg-card); color: var(--lg-ink);
  }
  .lg-dkorr:hover { background: var(--lg-tint); border-color: var(--lg-ink-mute); }
  .lg-dkorr:active { background: var(--lg-line-fein); }
  .lg-dkorr:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }

  /* ── Journal ──────────────────────────────────────────────────────────── */
  .lg-dabschnitt {
    margin: 24px 20px 8px;
    font-size: 11px; font-weight: 700; letter-spacing: 0.09em;
    text-transform: uppercase; color: var(--lg-ink-mute);
  }
  .lg-dleer {
    margin: 0 16px; padding: 20px;
    background: var(--lg-card); border-radius: 10px;
    font-size: 13px; color: var(--lg-ink-soft);
  }
  .lg-dkarte { background: var(--lg-card); overflow: hidden; }

  .lg-buch {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    box-sizing: border-box;
    min-height: 56px;
    padding: 10px 16px;
    border: 0;
    border-bottom: 1px solid var(--lg-line-fein);
    background: var(--lg-card);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .lg-buch:last-child { border-bottom: 0; }
  .lg-buch:hover { background: var(--lg-tint); }
  .lg-buch:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }

  /* Der Storno ist ein ZUSTAND der Zeile, keine zweite Zeile (Plan 17.3):
     halbe Deckkraft, Titel und Betrag durchgestrichen, Rücklauf-Icon. */
  .lg-buch.is-storno { opacity: 0.62; }
  .lg-buch.is-storno .lg-buch__art,
  .lg-buch.is-storno .lg-buch__delta { text-decoration: line-through; }
  .lg-buch.is-storno .lg-buch__delta { color: var(--lg-ink-mute); }

  .lg-buch__icon {
    width: 28px; height: 28px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    /* Abgang neutral, Eingang grün — die HiFi-Fassung (`.md-buch__icon`),
       nicht die des älteren `lager.css`, das dafür noch --warning-* führt.
       Dieselben Farben wie die Bewegungsliste der Bestandsansicht. */
    background: var(--lg-tick); color: var(--lg-ink-soft);
  }
  .lg-buch__icon.is-ein { background: var(--lg-ein-bg); color: var(--lg-ein-ink); }
  /* Eine zurückgenommene Buchung ist weder Zugang noch Abgang — sie ist
     nichts mehr, und die Farbe soll das nicht behaupten. */
  .lg-buch.is-storno .lg-buch__icon { background: var(--lg-tick); color: var(--lg-ink-mute); }

  .lg-buch__text { min-width: 0; }
  .lg-buch__art { display: block; font-size: 14px; font-weight: 600; color: var(--lg-ink); }
  .lg-buch__meta {
    display: block; margin-top: 2px;
    font-size: 11.5px; color: var(--lg-ink-mute);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  .lg-buch__zahlen { text-align: right; white-space: nowrap; }
  .lg-buch__delta {
    display: block;
    font-size: 14.5px; font-weight: 700; letter-spacing: -0.01em;
    color: var(--lg-ink-soft); font-variant-numeric: tabular-nums;
  }
  .lg-buch__delta.is-ein { color: var(--lg-ein-ink); }
  .lg-buch__stand {
    display: block; margin-top: 2px;
    font-size: 11px; color: var(--lg-ink-mute); font-variant-numeric: tabular-nums;
  }

  .lg-dalle {
    display: flex; align-items: center; justify-content: center;
    width: 100%; box-sizing: border-box; min-height: 48px;
    border: 0; border-top: 1px solid var(--lg-line-fein);
    background: var(--lg-tint);
    font: inherit; font-size: 13px; font-weight: 600; color: var(--lg-ink-soft);
    cursor: pointer;
  }
  .lg-dalle:hover { color: var(--lg-ink); }
  .lg-dalle:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }

  .lg-dnote {
    display: block; margin: 16px 16px 0;
    font-size: 11.5px; line-height: 1.5; color: var(--lg-ink-mute);
  }

  /* ── Ab 600 px ────────────────────────────────────────────────────────── */
  @container (min-width: 600px) {
    .lg-detail { padding-top: 16px; }
    .lg-zurueck { margin: 0 20px 6px; }

    /* Der Bestand rückt neben den Namen und wird rechtsbündig — so zeichnet
       ihn `zutat-detail.html`. Kleiner als mobil (30 statt 38 px): Er steht
       hier neben einer Überschrift, nicht allein auf einer Fläche. */
    .lg-dkopf {
      display: flex; align-items: flex-end; justify-content: space-between;
      gap: 20px; padding: 4px 20px 18px;
    }
    .lg-dname { font-size: 26px; }
    .lg-dwert { margin-top: 0; text-align: right; flex-shrink: 0; }
    .lg-dwert__zahl { justify-content: flex-end; }
    /* Die Knöpfe folgen der Zahl an den rechten Rand. Kein negativer Einzug
       mehr: Sie haben jetzt einen Rahmen, und der IST die Kante. */
    .lg-dtaten { justify-content: flex-end; margin-top: 10px; }
    .lg-dwert__zahl b { font-size: 30px; }

    .lg-dabschnitt { margin: 28px 20px 8px; }
    .lg-dkarte, .lg-dleer {
      border: 1px solid var(--lg-line);
      border-radius: 14px;
      margin: 0 20px;
    }
    .lg-buch { padding: 10px 20px; }
    /* Breit steht der Bestand groß im Kopf — der laufende Saldo je Zeile wäre
       dieselbe Auskunft ein zweites Mal (Plan 17.5). */
    .lg-buch__stand { display: none; }
    .lg-dnote { margin: 20px 20px 0; }
  }
</style>
